import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LEGO_COLORS, normalizeColorIndex } from "../constants/legoColors";

interface PixelBrickCanvasProps {
  pixels: number[][];
  width: number;
  height: number;
}

const BASEPLATE_COLOR = "#e0d9cd";

/**
 * The canvas is a viewport, not a poster: a big mosaic is scaled down so it
 * never pushes the page's controls below the fold.
 */
const MAX_CANVAS_HEIGHT_PX = 520;

const PixelBrickCanvas: React.FC<PixelBrickCanvasProps> = ({
  pixels,
  width,
  height,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(Math.floor(entries[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit the bricks to the container, but never taller than the canvas cap
  const brickSize = Math.max(
    Math.min(
      Math.floor(containerWidth / width),
      Math.floor(MAX_CANVAS_HEIGHT_PX / height),
    ),
    4,
  );
  const canvasW = width * brickSize;
  const canvasH = height * brickSize;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || pixels.length === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvasW;
    canvas.height = canvasH;

    ctx.fillStyle = BASEPLATE_COLOR;
    ctx.fillRect(0, 0, canvasW, canvasH);

    const bs = brickSize;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const ci = pixels[y]?.[x] ?? 0;
        const color = LEGO_COLORS[normalizeColorIndex(ci)];
        const px = x * bs;
        const py = y * bs;

        ctx.fillStyle = `rgb(${color.r},${color.g},${color.b})`;
        ctx.fillRect(px + 0.5, py + 0.5, bs - 1, bs - 1);

        ctx.fillStyle = "rgba(0,0,0,0.08)";
        ctx.fillRect(px + bs - 1.5, py + 0.5, 1, bs - 1);
        ctx.fillRect(px + 0.5, py + bs - 1.5, bs - 1, 1);

        ctx.fillStyle = "rgba(255,255,255,0.12)";
        ctx.fillRect(px + 0.5, py + 0.5, bs - 2, 1);
        ctx.fillRect(px + 0.5, py + 0.5, 1, bs - 2);

        if (bs >= 10) {
          const cx = px + bs / 2;
          const cy = py + bs / 2;
          const sr = bs * 0.2;

          ctx.beginPath();
          ctx.arc(cx, cy, sr, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.18)";
          ctx.fill();
          ctx.strokeStyle = "rgba(0,0,0,0.12)";
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }
  }, [pixels, width, height, canvasW, canvasH, brickSize]);

  return (
    <div ref={containerRef} className="w-full flex justify-center">
      <canvas
        ref={canvasRef}
        width={canvasW}
        height={canvasH}
        role="img"
        aria-label={t("converter.pixelatedVersion")}
        className="rounded-lg border-2 border-ink/10 shadow-soft max-w-full"
      />
    </div>
  );
};

export default React.memo(PixelBrickCanvas);
